import React from 'react';

import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Formatters, Messages } from '~src/codegen/Localize';
import { TooltipDraftIndicator } from '~src/drafts/components/TooltipDraftIndicator';
import { ContentDraftStatus } from '~src/drafts/serverApi/types/ContentDraft';
import { editPublishedConnectionDraft } from '~src/publishedConnection/actions/EditPublishedConnectionDraftReduxAction';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { DetailsTooltipStyles } from '~src/spacesPlatform/components/card/DetailsTooltipStyles';
import { MarkupField } from '~src/spacesPlatform/components/Explorer/widgets/MarkupField';
import { Name } from '~src/spacesPlatform/components/tooltip/Name';
import { RelatedEntity } from '~src/spacesPlatform/components/tooltip/RelatedEntity';
import { Timestamp } from '~src/spacesPlatform/components/tooltip/Timestamp';
import { Tooltip } from '~src/spacesPlatform/components/tooltip/Tooltip';
import { ContentTooltipProps, getProjectLink, TooltipDescriptionTestId } from '~src/spacesPlatform/components/tooltip/TooltipHelper';
import { User } from '~src/spacesPlatform/components/tooltip/User';
import { Tags } from '~src/tags/components/tooltips/Tags';

import { IPublishedConnectionInfo } from '../../models/IPublishedConnectionInfo';

export function PublishedConnectionTooltip(props: ContentTooltipProps): JSX.Element {
  const { content, urls } = props;
  const publishedConnectionInfo = content as IPublishedConnectionInfo;
  const projectLink = getProjectLink(publishedConnectionInfo.project, urls);
  const lastUpdatedAtFormatted: string = Formatters.formatLongBestFitRelativeTime(new Date(publishedConnectionInfo.updatedAt));

  const hideEditingLink = React.useMemo(() => {
    if (publishedConnectionInfo.draftStatus === ContentDraftStatus.unpublished) {
      const server = getSessionManagementServiceInstance();
      if (content.owner.id !== server.sessionUserId()) {
        return  true;
      }
    }
    return false;
  }, [content.owner.id, publishedConnectionInfo.draftStatus]);

  const handleEditDraft = () => {
    if (publishedConnectionInfo.draftLuid) {
      getAppStore().dispatch(editPublishedConnectionDraft(publishedConnectionInfo.draftLuid));
    }
  };

  return (
    <Tooltip>
      <Name name={content.name} url={props.contentUrl}/>
      <MarkupField
        markup={props.content.description}
        testId={TooltipDescriptionTestId}
        extraClasses={[DetailsTooltipStyles.MetadataSectionWithContentDescription]}/>
      <dl className={DetailsTooltipStyles.DescriptionList}>
        <Tags tags={props.content.tags}/>
        {!props.inProjectSpace && <RelatedEntity label={Messages.project()} link={projectLink}/>}
        <User user={props.content.owner} label={Messages.owner()} urls={urls}/>
        <Timestamp label={Messages.modified()} value={props.content.updatedAt}/>
      </dl>
      {publishedConnectionInfo.draftStatus !== ContentDraftStatus.none &&
        <TooltipDraftIndicator
          indicatorText={() => Messages.draftLastEditIndicatorText({LAST_EDITED: lastUpdatedAtFormatted})}
          linkText={Messages.continueEditing}
          handleAction={handleEditDraft}
          hideEditingLink={hideEditingLink}
        />}
    </Tooltip>
);
}
